import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CommunityLayout from '../../components/community/CommunityLayout';
import NoticeCard from '../../components/common/NoticeCard';
import { usePosts } from '../../hooks/usePosts';
import { searchCommunityPosts } from '../../services/searchApi';
import type { PostSearchItem } from '../../services/searchApi';

const CommunityHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // API를 통한 게시글 목록 조회 (실패 시 더미 데이터 사용)
  const { data: postsData, loading } = usePosts({
    board: 'home',
    page: 0,
    size: 10
  });

  const posts = postsData?.content || [];

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchCommunityPosts(keyword);
      setSearchResults(results);
    } catch (error) {
      console.error('커뮤니티 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/community?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/community');
      }
    }
  };

  const handlePostClick = (postId: number) => {
    navigate(`/community/post/${postId}`);
  };

  // HTML 태그 제거 함수 (코드, 이미지, 첨부파일은 완전히 제거)
  const stripHtmlExceptCodeAndMedia = (html: string): string => {
    // 임시 div 엘리먼트 생성
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // 코드 블록 완전히 제거
    const preElements = tempDiv.querySelectorAll('pre');
    preElements.forEach((pre) => {
      pre.remove();
    });
    
    // 인라인 코드 완전히 제거
    const codeElements = tempDiv.querySelectorAll('code');
    codeElements.forEach((code) => {
      code.remove();
    });
    
    // 이미지 태그 완전히 제거
    const imgElements = tempDiv.querySelectorAll('img');
    imgElements.forEach((img) => {
      img.remove();
    });
    
    // 첨부파일 관련 모든 요소 완전히 제거
    const attachmentLists = tempDiv.querySelectorAll('ul.tbc-attachments, ul');
    attachmentLists.forEach((ul) => {
      // 첨부파일 목록인지 확인 (li 안에 a 태그가 있는 경우)
      const hasAttachmentLinks = ul.querySelectorAll('li a').length > 0;
      if (hasAttachmentLinks) {
        ul.remove();
      }
    });
    
    // 모든 종류의 첨부파일 링크 완전히 제거
    const attachmentLinks = tempDiv.querySelectorAll('a[download], a[href*="blob:"], a[href*=".pdf"], a[href*=".doc"], a[href*=".zip"], a[href*=".jpg"], a[href*=".png"], a[href*=".gif"]');
    attachmentLinks.forEach((link) => {
      link.remove();
    });
    
    // 첨부파일 관련 클래스가 있는 모든 요소 제거
    const attachmentElements = tempDiv.querySelectorAll('.tbc-attachment, .tbc-attachment__link, .tbc-attachment__name, .attachment');
    attachmentElements.forEach((element) => {
      element.remove();
    });
    
    // 파일 확장자가 포함된 링크나 텍스트 제거
    const fileExtensionPattern = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z|tar|gz|jpg|jpeg|png|gif|bmp|svg|mp4|avi|mov|mp3|wav|txt|csv)$/i;
    const allLinks = tempDiv.querySelectorAll('a');
    allLinks.forEach((link) => {
      const href = link.getAttribute('href') || '';
      const text = link.textContent || '';
      if (fileExtensionPattern.test(href) || fileExtensionPattern.test(text)) {
        link.remove();
      }
    });
    
    // 나머지 HTML 태그만 제거하고 텍스트 내용 추출
    let textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // 연속된 공백과 줄바꿈 정리
    return textContent.replace(/\s+/g, ' ').trim();
  };

  // HTML 태그 제거 및 텍스트 요약
  const getPlainText = (html: string, maxLength: number = 150) => {
    const cleanText = stripHtmlExceptCodeAndMedia(html);
    return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
  };

  return (
    <CommunityLayout hideFooter={true}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>커뮤니티</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티 홈</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력해주세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 공지사항 섹션 */}
          <NoticeSection>
            <NoticeCard title="상단고정 공지사항" />
            <NoticeCard title="상단고정 공지사항" />
          </NoticeSection>

          {/* 게시글 목록 */}
          <PostsList>
            {isSearchMode ? (
              // 검색 모드
              <>
                {searchLoading ? (
                  <EmptyState>
                    <EmptyMessage>검색 중...</EmptyMessage>
                  </EmptyState>
                ) : searchError ? (
                  <EmptyState>
                    <EmptyMessage>{searchError}</EmptyMessage>
                    <EmptySubMessage>다시 시도해보세요.</EmptySubMessage>
                  </EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>
                    <EmptyMessage>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyMessage>
                    <EmptySubMessage>다른 검색어로 다시 검색해보세요.</EmptySubMessage>
                  </EmptyState>
                ) : (
                  searchResults.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <PostCard onClick={() => handlePostClick(post.id)}>
                        <PostContent>
                          <PostHeader>
                            <PostTitle>{post.title}</PostTitle>
                            <PostDescription>
                              {getPlainText(post.htmlcontent)}
                            </PostDescription>
                          </PostHeader>
                          
                          <PostMeta>
                            <PostCategoryTags>
                              <PostCategory>{post.category}</PostCategory>
                              {post.lectureTitle && <PostCategory>{post.lectureTitle}</PostCategory>}
                              {post.pinned && <PostCategory style={{backgroundColor: '#ff6b6b', color: 'white'}}>📌 고정</PostCategory>}
                            </PostCategoryTags>
                          </PostMeta>
                        </PostContent>
                        
                        <PostFooter>
                          <PostAuthorInfo>
                            <AuthorAvatar>
                              <img src={post.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                            </AuthorAvatar>
                            <AuthorName>{post.writerName}</AuthorName>
                            <Divider />
                            <PostDate>{new Date(post.postDate).toLocaleDateString('ko-KR')}</PostDate>
                          </PostAuthorInfo>
                          
                          <PostStats>
                            <StatItem>댓글 -</StatItem>
                            <Divider />
                            <StatItem>조회수 -</StatItem>
                          </PostStats>
                        </PostFooter>
                      </PostCard>
                      {index < searchResults.length - 1 && <PostDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            ) : (
              // 일반 모드 (기본 데이터)
              <>
                {loading ? (
                  <EmptyState>
                    <EmptyMessage>게시글을 불러오는 중...</EmptyMessage>
                  </EmptyState>
                ) : posts.length === 0 ? (
                  <EmptyState>
                    <EmptyMessage>아직 작성된 게시글이 없습니다.</EmptyMessage>
                    <EmptySubMessage>첫 번째 글을 작성해보세요!</EmptySubMessage>
                  </EmptyState>
                ) : (
                  posts.map((post, index) => (
                    <React.Fragment key={post.post_id}>
                      <PostCard onClick={() => handlePostClick(post.post_id)}>
                        <PostContent>
                          <PostHeader>
                            <PostTitle>{post.title}</PostTitle>
                            <PostDescription>
                              {getPlainText(post.content)}
                            </PostDescription>
                          </PostHeader>
                          
                          <PostMeta>
                            <PostCategoryTags>
                              <PostCategory>{post.post_category}</PostCategory>
                              {post.pinned && <PostCategory style={{backgroundColor: '#ff6b6b', color: 'white'}}>📌 고정</PostCategory>}
                            </PostCategoryTags>
                          </PostMeta>
                        </PostContent>
                        
                        <PostFooter>
                          <PostAuthorInfo>
                            <AuthorAvatar>
                              <img src="/images/default-avatar.png" alt="작성자" />
                            </AuthorAvatar>
                            <AuthorName>{post.writer_name}</AuthorName>
                            <Divider />
                            <PostDate>{new Date(post.created_at).toLocaleDateString('ko-KR')}</PostDate>
                          </PostAuthorInfo>
                          
                          <PostStats>
                            <StatItem>댓글 {post.reply_cnt}</StatItem>
                            <Divider />
                            <StatItem>조회수 {post.view_cnt}</StatItem>
                          </PostStats>
                        </PostFooter>
                      </PostCard>
                      {index < posts.length - 1 && <PostDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            )}
          </PostsList>
        </MainContainer>
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center; /* 중앙 정렬 */
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px; /* 콘텐츠 영역의 최대 너비 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: rgba(55, 56, 60, 0.28);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const NoticeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PostsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PostCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(112, 115, 124, 0.02);
  }
`;

const PostContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PostHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PostTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const PostDescription = styled.p`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  margin: 0;
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const PostCategoryTags = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const PostCategory = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PostAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const PostDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyMessage = styled.div`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
`;

const EmptySubMessage = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

export default CommunityHome;